import React from 'react'
import Modal from 'react-bootstrap/Modal'

import AuthManager from "../../../utils/AuthManager"

export default class AIQueryResponse extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      show: props.show,
      loading: true,
      response: '',
      sources: [],
      error: null,
      streamingComplete: false,
      showTimeSources: false,
    }
    this._eventSource = null
  }

  componentDidMount() {
    if (this.props.show) {
      this._startStreaming()
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.show && !this.props.show) {
      this.setState({
        show: true,
        loading: true,
        response: '',
        sources: [],
        error: null,
        streamingComplete: false,
      }, () => this._startStreaming())
    } else if (!nextProps.show) {
      this._closeStream()
      this.setState({ show: false })
    }
  }

  componentWillUnmount() {
    this._closeStream()
  }

  _extractUUID() {
    const url = this.props.linkie?.url || ''
    const parts = url.split('/ai-queries/')
    return parts.length > 1 ? parts[1].replace(/\/$/, '') : null
  }

  _startStreaming() {
    const uuid = this._extractUUID()
    if (!uuid) {
      this.setState({ error: 'Could not determine AI query ID.', loading: false })
      return
    }

    this._closeStream()

    const url = `${window.Api.AIQueries}/${uuid}/stream`
    const headers = {}
    if (AuthManager.accessToken) {
      headers['Authorization'] = 'Bearer ' + AuthManager.accessToken
    }

    fetch(url, { headers })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch AI response`)
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        this._reader = reader

        const read = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              this.setState({ loading: false, streamingComplete: true })
              return
            }

            const text = decoder.decode(value, { stream: true })
            const lines = text.split('\n')

            lines.forEach(line => {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6))
                  if (data.chunk != null) {
                    this.setState(prev => ({
                      response: prev.response + data.chunk,
                      loading: false,
                    }))
                  }
                  if (data.done) {
                    this.setState(prev => ({
                      response: data.full_response || prev.response,
                      sources: data.sources || [],
                      loading: false,
                      streamingComplete: true,
                    }))
                  }
                  if (data.error) {
                    this.setState({ error: data.error, loading: false })
                  }
                } catch (e) {
                  // ignore parse errors for incomplete chunks
                }
              }
            })

            read()
          }).catch(err => {
            if (err.name !== 'AbortError') {
              this.setState({ error: err.message, loading: false })
            }
          })
        }

        read()
      })
      .catch(err => {
        this.setState({ error: err.message, loading: false })
      })
  }

  _closeStream() {
    if (this._reader) {
      try {
        this._reader.cancel()
      } catch (e) {}
      this._reader = null
    }
  }

  _parseResponse(response) {
    if (!response) return { mainContent: '', timeNote: '', sources: '' }

    let mainContent = response
    let timeNote = ''
    let sources = ''

    // Extract Sources section
    const sourcesMatch = mainContent.match(/\n*(?:#{1,3}\s*)?Sources:\s*\n([\s\S]*)$/i)
    if (sourcesMatch) {
      sources = sourcesMatch[1].trim()
      mainContent = mainContent.substring(0, sourcesMatch.index).trim()
    }

    // Extract Time window note line(s)
    const timeNoteRegex = /\n*\*?\*?Time window note:?\*?\*?\s*.*$/im
    const timeMatch = mainContent.match(timeNoteRegex)
    if (timeMatch) {
      timeNote = timeMatch[0].replace(/^\n+/, '').trim()
      mainContent = mainContent.substring(0, timeMatch.index).trim()
    }

    return { mainContent, timeNote, sources }
  }

  _renderMarkdown(text) {
    // Simple markdown rendering: bold, italic, code blocks, links, headers, lists
    const lines = text.split('\n')
    const elements = []
    let inCodeBlock = false
    let codeContent = ''

    lines.forEach((line, i) => {
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={`code-${i}`} className="ai-code-block">
              <code>{codeContent}</code>
            </pre>
          )
          codeContent = ''
          inCodeBlock = false
        } else {
          inCodeBlock = true
        }
        return
      }

      if (inCodeBlock) {
        codeContent += (codeContent ? '\n' : '') + line
        return
      }

      // Headers
      if (line.startsWith('### ')) {
        elements.push(<h5 key={i} className="mt-3 mb-1">{this._renderInline(line.slice(4))}</h5>)
      } else if (line.startsWith('## ')) {
        elements.push(<h4 key={i} className="mt-3 mb-1">{this._renderInline(line.slice(3))}</h4>)
      } else if (line.startsWith('# ')) {
        elements.push(<h3 key={i} className="mt-3 mb-2">{this._renderInline(line.slice(2))}</h3>)
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        elements.push(<li key={i}>{this._renderInline(line.slice(2))}</li>)
      } else if (/^\d+\.\s/.test(line)) {
        elements.push(<li key={i}>{this._renderInline(line.replace(/^\d+\.\s/, ''))}</li>)
      } else if (line.trim() === '') {
        elements.push(<br key={i} />)
      } else {
        elements.push(<p key={i} className="mb-1">{this._renderInline(line)}</p>)
      }
    })

    // Close any unclosed code block
    if (inCodeBlock && codeContent) {
      elements.push(
        <pre key="code-end" className="ai-code-block">
          <code>{codeContent}</code>
        </pre>
      )
    }

    return elements
  }

  _renderInline(text) {
    const parts = []
    let remaining = text
    let key = 0

    while (remaining.length > 0) {
      // Match: parenthesized markdown link ([text](url)), markdown link [text](url), or inline code `code`
      let parenLinkMatch = remaining.match(/\(\[([^\]]+)\]\((https?:\/\/[^\s)]*(?:\([^\s)]*\))*[^\s)]*)\)\)/)
      let linkMatch = remaining.match(/\[([^\]]+)\]\((https?:\/\/[^\s)]*(?:\([^\s)]*\))*[^\s)]*)\)/)
      let codeMatch = remaining.match(/`([^`]+)`/)

      // Get indices
      let parenLinkIdx = parenLinkMatch ? remaining.indexOf(parenLinkMatch[0]) : Infinity
      let linkIdx = linkMatch ? remaining.indexOf(linkMatch[0]) : Infinity
      let codeIdx = codeMatch ? remaining.indexOf(codeMatch[0]) : Infinity

      // Find earliest match
      let minIdx = Math.min(parenLinkIdx, linkIdx, codeIdx)

      if (minIdx === Infinity) {
        parts.push(this._processFormats(remaining, key++))
        break
      }

      if (parenLinkIdx === minIdx) {
        if (parenLinkIdx > 0) parts.push(this._processFormats(remaining.slice(0, parenLinkIdx), key++))
        parts.push(<span key={key++}>(</span>)
        parts.push(
          <a key={key++} href={parenLinkMatch[2]} target="_blank" rel="noopener noreferrer" style={{ color: '#2563EB', textDecoration: 'none' }}>
            {parenLinkMatch[1]}
          </a>
        )
        parts.push(<span key={key++}>)</span>)
        remaining = remaining.slice(parenLinkIdx + parenLinkMatch[0].length)
      } else if (linkIdx === minIdx) {
        if (linkIdx > 0) parts.push(this._processFormats(remaining.slice(0, linkIdx), key++))
        parts.push(
          <a key={key++} href={linkMatch[2]} target="_blank" rel="noopener noreferrer" style={{ color: '#2563EB', textDecoration: 'none' }}>
            {linkMatch[1]}
          </a>
        )
        remaining = remaining.slice(linkIdx + linkMatch[0].length)
      } else {
        if (codeIdx > 0) parts.push(this._processFormats(remaining.slice(0, codeIdx), key++))
        parts.push(<code key={key++} className="ai-inline-code">{codeMatch[1]}</code>)
        remaining = remaining.slice(codeIdx + codeMatch[0].length)
      }
    }

    return parts
  }

  _processFormats(text, key) {
    // Bold and italic
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    text = text.replace(/\*(.+?)\*/g, '<em>$1</em>')
    return <span key={key} dangerouslySetInnerHTML={{ __html: text }} />
  }

  render() {
    const { show, loading, response, error, streamingComplete } = this.state
    const linkie = this.props.linkie || {}

    return (
      <Modal
        show={show}
        onHide={() => {
          this._closeStream()
          this.props.onHide()
        }}
        size="lg"
        centered
        className="ai-response-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center">
              <span className="ai-query-icon-sm me-2" style={{
                width: 32, height: 32, borderRadius: '6px',
                backgroundColor: '#6f55ff', display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center'
              }}>
                <img
                  src={require('../../../assets/media/icons/ai.png')}
                  alt="AI"
                  style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                />
              </span>
              <span>{linkie.name || 'AI Query Response'}</span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {linkie.prompt && (
            <div className="ai-prompt-section mb-3 p-3" style={{
              backgroundColor: '#6f55ff',
              borderRadius: '8px',
              color: '#ffffff'
            }}>
              <p className="mb-0">{linkie.prompt}</p>
            </div>
          )}

          {error && (
            <div className="alert alert-danger">{error}</div>
          )}

          {loading && !response && (
            <div className="text-center py-5 d-flex align-items-center justify-content-center">
              <span style={{ color: '#6f55ff', fontSize: '18px', fontWeight: '500' }}>Thinking</span>
              <span className="ai-dots-container" style={{ display: 'inline-flex', marginLeft: '6px', gap: '4px', alignItems: 'center' }}>
                <span className="ai-dot" style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fe2d9a', animation: 'aiBounce 1.2s infinite ease-in-out', animationDelay: '0s' }}></span>
                <span className="ai-dot" style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#6f55ff', animation: 'aiBounce 1.2s infinite ease-in-out', animationDelay: '0.3s' }}></span>
                <span className="ai-dot" style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fe7312', animation: 'aiBounce 1.2s infinite ease-in-out', animationDelay: '0.6s' }}></span>
              </span>
              <style>{`
                @keyframes aiBounce {
                  0%, 100% { transform: scale(0.5); opacity: 0.5; }
                  50% { transform: scale(1.2); opacity: 1; }
                }
              `}</style>
            </div>
          )}

          {response && (() => {
            const { mainContent, timeNote, sources: parsedSources } = this._parseResponse(response)
            const hasTimeSources = streamingComplete && !!(timeNote || parsedSources)
            return (
              <>
                <div className="ai-response-content" style={{ lineHeight: '1.7' }}>
                  {this._renderMarkdown(mainContent || response)}
                </div>

                {hasTimeSources && (
                  <div className="text-center mt-3">
                    <button
                      className="btn btn-link text-muted text-decoration-none"
                      style={{ fontSize: '14px', fontWeight: '500', color: '#9CA3AF' }}
                      onClick={() => this.setState({ showTimeSources: !this.state.showTimeSources })}
                    >
                      Time &amp; Sources
                    </button>
                    {this.state.showTimeSources && (
                      <div className="mt-2 text-start" style={{ fontSize: '13px', color: '#666', lineHeight: '1.6' }}>
                        {!!timeNote && (
                          <div className="mb-2">{this._renderMarkdown(timeNote)}</div>
                        )}
                        {!!parsedSources && (
                          <div>{this._renderMarkdown(parsedSources)}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )
          })()}

          {!loading && streamingComplete && !response && !error && (
            <p className="text-muted text-center">No response available.</p>
          )}
        </Modal.Body>
      </Modal>
    )
  }
}
